import { WageFrequency } from "@api/shared-runtime/enums";
import { formatCurrency } from "@/features/expense/utils/expense-schema";
import { formatWage } from "../../utils/custom-wage";
import type { AdHocPayPreview } from "../../utils/ad-hoc-pay-preview";
import { formatHours } from "../../org/utils/group-time-entries";

const PAY_SUMMARY_LABEL = "Pay summary";
const WAGE_ROW_LABEL = "Wage";
const HOURS_ROW_LABEL = "Hours";
const TOTAL_LABEL = "Total";
const CUSTOM_WAGE_TOTAL_LABEL = "Total if approved as submitted";
const PER_SHIFT_NOTE =
  "Per-shift wage: paid once, however many hours you work.";
const CUSTOM_WAGE_NOTE =
  "The organization may change this wage when it reviews your hours.";

interface AdHocPaySummaryProps {
  preview: AdHocPayPreview;
  isCustomWage: boolean;
}

/** The pay a provider can expect, shown just before they submit hours. */
export function AdHocPaySummary({
  preview,
  isCustomWage,
}: AdHocPaySummaryProps) {
  const isPerShift = preview.wageFrequencyId === WageFrequency.OneTime;
  // Only a custom wage is open to change at review, so only it needs the
  // "as submitted" qualifier on the total.
  const totalLabel = isCustomWage ? CUSTOM_WAGE_TOTAL_LABEL : TOTAL_LABEL;

  return (
    <section
      aria-label={PAY_SUMMARY_LABEL}
      className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3"
    >
      <dl className="space-y-1.5 text-sm">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-slate-600">{WAGE_ROW_LABEL}</dt>
          <dd className="text-right text-slate-900">
            <span className="block font-medium">{formatWage(preview)}</span>
            <span className="block text-xs text-slate-500">
              {preview.wageLabel}
            </span>
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-slate-600">{HOURS_ROW_LABEL}</dt>
          <dd className="text-right text-slate-900">
            {formatHours(preview.hours)}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 border-t border-slate-200 pt-1.5">
          <dt className="font-semibold text-slate-900">{totalLabel}</dt>
          <dd className="text-right text-base font-semibold text-slate-900">
            {formatCurrency(preview.total)}
          </dd>
        </div>
      </dl>
      {isPerShift && <p className="text-xs text-slate-500">{PER_SHIFT_NOTE}</p>}
      {isCustomWage && (
        <p className="text-xs text-slate-500">{CUSTOM_WAGE_NOTE}</p>
      )}
    </section>
  );
}
